import * as React from 'react';
import {
    Box,
    Button,
    Typography,
    Dialog,
    DialogContent,
    DialogActions,
    List,
    IconButton,
    DialogTitle
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from "@mui/material/InputBase";
import AddIcon from '@mui/icons-material/Add';
import PublishIcon from '@mui/icons-material/Publish';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import Tooltip from '@mui/material/Tooltip';
import {
    GridToolbar,
    GridActionsCellItem,
    DataGridPremium,
    GRID_AGGREGATION_FUNCTIONS,
    useGridApiRef
} from "@mui/x-data-grid-premium";
import {tokens} from "../theme";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import Header from "../components/header";
import FeatureWizard from "../components/featureWizard";

import {apiCall, apiFileDownload, apiFileUpload} from "../utils/network";
import {InputDataTypes, HttpError} from "shared";
import {getIconElement} from '../utils/icons';
import {isNullOrEmpty} from '../utils/string';
import {getInnerElement} from '../utils/componentHelper';
import PropTypes from "prop-types";

const FeaturePage = ({backdropOpened, gameKey, env, theme, feature, setBackdropOpened }) => {

    // Feature data
    const [featureData, setFeatureData] = React.useState([]);

    // This is used to open the creation wizard
    const [createMode, setCreateMode] = React.useState(false);

    // This is used control the selectedRows
    const [selectedRows, setSelectedRows] = React.useState([]);
    const [selectedRowsDeleteRequest, setSelectedRowsDeleteRequest] = React.useState(false);

    // This is used to handle errors after an api call
    const [errors, setErrors] = React.useState(null);

    // This is used to 'GetRowValues' row button
    const [rowValuesData, setRowValuesData] = React.useState(undefined);

    // These are used to handle editing a field in a row
    const noButtonRef = React.useRef(null);
    const [rowCellUpdatePromiseArguments, setRowCellUpdatePromiseArguments] = React.useState(null);

    // This is used to handle row button click (for buttons that actually change something in the db)
    const [rowButtonPromiseArguments, setRowButtonPromiseArguments] = React.useState(null);
    const [rowButtonInputs, setRowButtonInputs] = React.useState([]);
    React.useEffect(() => {
        fetchData(gameKey, env, feature, setFeatureData, setBackdropOpened);
    }, [gameKey, env, feature, setFeatureData, setBackdropOpened]);

    // handle primary keys
    const primaryKeys = getPrimaryKeys(feature);

    // handle columns
    const columns = [];
    const aggregationModel = {};
    
    const getRowButtons = (params, feature) => {
        let res = [];
        if (feature.rowButtons?.length > 0) {
            feature.rowButtons.forEach(rowButton => {
                let onClickFunc = rowButton.popup
                    ? () => handleActionButtonClick(params.id, rowButton.id)
                    : () => handleRowButtonAction(params.id, gameKey, env, setFeatureData, primaryKeys, setBackdropOpened, feature, rowButton, setRowValuesData);

                res.push(<Tooltip title={rowButton.hoverText}><GridActionsCellItem key={rowButton.id.toString()}
                                                                                   icon={getIconElement(rowButton.iconType)}
                                                                                   onClick={onClickFunc}
                                                                                   label={rowButton.id.toString()}/></Tooltip>);
            });
        }
        return res;
    };

    getFieldsArray(feature).forEach(field => {
        columns.push({
            headerName: field.displayName,
            field: field.key,
            flex: getFlex(field.type),
            type: getType(field.type),
            editable: field.editable
        });
        aggregationModel[field.key] = "equalValues";
    });
    if (feature.rowButtons?.length > 0) {
        columns.push({
            field: "actions",
            type: "actions",
            align: "right",
            headerAlign: "center",
            headerName: "Actions",
            minWidth: "50px",
            flex: 0.4 * feature.rowButtons.length,
            getActions: (params) => getRowButtons(params, feature)
        });
    }

    const apiRef = useGridApiRef();
    let rowGroupingModel = primaryKeys; // atm grouping is only on the first property (id property)
    const initialStateObj = ({
        apiRef,
        initialState: {
            rowGrouping: {
                model: rowGroupingModel,
            }
        }
    });

    if (!isGroupingDisabled(feature)) {
        initialStateObj.columns = {
            columnVisibilityModel: primaryKeys.reduce((acc, key) => ({ ...acc, [key]: false }), {})
        };

        initialStateObj.aggregation = {
            model: {
                1: 'profit'
            }
        };
    }
    const getActionButtons = (feature, colors) => {

        let res = [];

        if (selectedRows?.length > 0 && feature.wizardData.showDeleteMultipleButton) {
            res.push(<Button
                key={'delete'}
                onClick={() => setSelectedRowsDeleteRequest(true)}
                sx={{
                    backgroundColor: colors.blueAccent[700],
                    color: colors.grey[100],
                    fontSize: "14px",
                    fontWeight: "bold",
                    padding: "10px 20px",
                    border: 1, borderRadius: '10%', borderColor: colors.primary[400]
                }}
            >
                <DeleteIcon sx={{mr: "10px"}}/>
                Delete Selected Rows
            </Button>);
        }

        if (feature.wizardData.creationMethods?.includes("bulk")) {
            res.push(<Button
                key={'download'}
                onClick={() => downloadTemplateCSV(gameKey, env, feature, setBackdropOpened)}
                sx={{
                    backgroundColor: colors.blueAccent[700],
                    color: colors.grey[100],
                    fontSize: "14px",
                    fontWeight: "bold",
                    padding: "10px 20px",
                    border: 1, borderRadius: '10%', borderColor: colors.primary[400]
                }}
            >
                <DownloadIcon sx={{mr: "10px"}}/>
                Download CSV
            </Button>);

            res.push(<Button
                component="label"
                key={'upload'}
                sx={{
                    backgroundColor: colors.blueAccent[700],
                    color: colors.grey[100],
                    fontSize: "14px",
                    fontWeight: "bold",
                    padding: "10px 20px",
                    border: 1, borderRadius: '10%', borderColor: colors.primary[400]
                }}
            >
                <PublishIcon sx={{mr: "10px"}}/>
                Import
                <input hidden accept=".xlsx" type="file"
                       onChange={(event) => importFromTemplateCSV(event.target.files[0], gameKey, env, feature, setFeatureData, setBackdropOpened, setErrors)}/>
            </Button>);
        }

        if (feature.wizardData.creationMethods?.includes("manual")) {
            res.push(<Button
                key={'create'}
                onClick={() => setCreateMode(true)}
                sx={{
                    backgroundColor: colors.blueAccent[700],
                    color: colors.grey[100],
                    fontSize: "14px",
                    fontWeight: "bold",
                    padding: "10px 20px",
                    border: 1, borderRadius: '10%', borderColor: colors.primary[400]
                }}
            >
                <AddIcon sx={{mr: "10px"}}/>
                Create
            </Button>);
        }

        return res;
    };

    const initialState = initialStateObj;
    const colors = tokens(theme.palette.mode);

    // create search elements
    let searchElements = [];
    let filters = feature.fields.filter(x => x.useAsSearchFilter);
    if (filters?.length > 0) {
        let fieldIdToHook = {};
        filters.forEach(field => {
            const [searchFieldValue, setSearchFieldValue] = React.useState("");
            fieldIdToHook[field.id] = setSearchFieldValue;
            searchElements.push(<Box key={`search_${field.id}`} backgroundColor={colors.blueAccent[700]}
                                     sx={{border: 1, borderRadius: '5%', borderColor: colors.primary[400]}}>
                <InputBase sx={{ml: 2, flex: 1 / filters.length}} placeholder={`Enter ${field.displayName}...`}
                           value={searchFieldValue} onChange={(params) => {
                    setSearchFieldValue(params.target.value);
                }}
                           onKeyUp={(params) => {
                               if (params.key === 'Enter') {
                                   clearOtherSearchFields(field.id, fieldIdToHook);
                                   handleSearchRequest(gameKey, env, feature, setFeatureData, setBackdropOpened, field.id, searchFieldValue);
                               }
                           }}/>
                <IconButton type="button" sx={{p: 1}} onClick={() => {
                    clearOtherSearchFields(field.id, fieldIdToHook);
                    handleSearchRequest(gameKey, env, feature, setFeatureData, setBackdropOpened, field.id, searchFieldValue);
                }}><SearchIcon/></IconButton>
            </Box>);
        });
    }

    const processRowUpdate = React.useCallback(
        (newRow, oldRow) =>
            new Promise((resolve) => {
                let diff = getRowsDiffBodyAndMutation(feature, newRow, oldRow);
                if (diff) {
                    setRowCellUpdatePromiseArguments({newRow, oldRow, resolve});
                } else {
                    resolve(oldRow); // Nothing was changed
                }
            }),
        [],
    );

    const renderRowUpdateConfirmDialog = () => {
        if (!rowCellUpdatePromiseArguments) {
            return null;
        }

        const handleEntered = () => {
            // The `autoFocus` is not used because, if used, the same Enter that saves
            // the cell triggers "No". Instead, we manually focus the "No" button once
            // the dialog is fully open.
            // noButtonRef.current?.focus();
        };

        const handleNo = () => {
            const {oldRow, resolve} = rowCellUpdatePromiseArguments;
            resolve(oldRow); // Resolve with the old row to not update the internal state
            setRowCellUpdatePromiseArguments(null);
        };

        const handleYes = async () => {
            const {newRow, oldRow, resolve} = rowCellUpdatePromiseArguments;
            try {
                setBackdropOpened(true);
                const diff = getRowsDiffBodyAndMutation(feature, newRow, oldRow);
                const params = getIdsParams(feature, getManipulatedRowId(oldRow), primaryKeys);
                params["featureId"] = feature.id;
                setRowCellUpdatePromiseArguments(null);
                await apiCall({
                    method: 'PUT',
                    api: "features",
                    params,
                    body: diff[0],
                    environment: env,
                    gameKey: gameKey
                });
                resolve(newRow);
                setBackdropOpened(false);
            } catch (error) {
                resolve(oldRow);
                setErrors({title: 'Update Failed!!!', errors: [error.message]});
                setRowCellUpdatePromiseArguments(null);
                setBackdropOpened(false);
            }
        };

        const {newRow, oldRow} = rowCellUpdatePromiseArguments;
        const mutation = getRowsDiffBodyAndMutation(feature, newRow, oldRow)[1];

        return (
            <Dialog
                maxWidth="xs"
                TransitionProps={{onEntered: handleEntered}}
                open={!!rowCellUpdatePromiseArguments}
            >
                <DialogTitle backgroundColor={colors.primary[400]}>Are you sure?</DialogTitle>
                <DialogContent dividers>
                    {`Pressing 'Yes' will change ${mutation}.`}
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="secondary" ref={noButtonRef} onClick={handleNo}>
                        No
                    </Button>
                    <Button variant="contained" color="secondary" onClick={handleYes}>Yes</Button>
                </DialogActions>
            </Dialog>
        );
    };

    const renderSelectedRowsActionConfirmDialog = () => {
        if (!selectedRowsDeleteRequest) {
            return null;
        }

        const handleEntered = () => {
            // The `autoFocus` is not used because, if used, the same Enter that saves
            // the cell triggers "No". Instead, we manually focus the "No" button once
            // the dialog is fully open.
            // noButtonRef.current?.focus();
        };

        const handleNo = () => {
            setSelectedRowsDeleteRequest(false);
            setSelectedRows([]);
        };

        const handleYes = async () => {
            try {
                if(selectedRows.length === 0)
                    return;

                console.log(`*********** DELETE + ${selectedRows} ***********`);
                setBackdropOpened(true);
                let fieldIdToValue = {};
                selectedRows.forEach(rowId => {
                    let idObj = getIdsParams(feature, rowId, primaryKeys);
                    for (let i = 0; i < primaryKeys.length; i++) {
                        if(!fieldIdToValue[primaryKeys[i]])
                            fieldIdToValue[primaryKeys[i]] = [];

                        fieldIdToValue[primaryKeys[i]].push(idObj[primaryKeys[i]]);
                    }
                });
                let params = {};
                Object.keys(fieldIdToValue).forEach(key => {
                    params[key] = fieldIdToValue[key].join(',');
                });
                setSelectedRows([]);
                setSelectedRowsDeleteRequest(false);
                console.log(`***********************  ${params}   **************************`);

                params["featureId"] = feature.id;
                
                try {
                    await apiCall({
                        method: 'DELETE',
                        api: "features/multiple",
                        params,
                        environment: env,
                        gameKey: gameKey
                    });
                    await fetchData(gameKey, env, feature, setFeatureData, setBackdropOpened);
                } catch (error) {
                    if (error instanceof HttpError && error.extraData) {
                        setErrors({
                            title: error.message,
                            errors: error.extraData.map(x => `${x.location} | ${x.message}`)
                        });
                    } else {
                        setErrors({
                            title: error.message,
                            errors: [error.message]
                        });
                    }
                }
                setBackdropOpened(false);
            } catch (error) {
                
                setErrors({title: 'Update Failed!!!', errors: [error.message]});
                setSelectedRowsDeleteRequest(false);
                setBackdropOpened(false);
            }
        };

        return (
            <Dialog
                maxWidth="xs"
                TransitionProps={{onEntered: handleEntered}}
                open={!!selectedRowsDeleteRequest}
            >
                <DialogTitle backgroundColor={colors.primary[400]}>Are you sure you want to delete multiple rows?</DialogTitle>
                <DialogContent dividers>
                    {`This cannot be undone!`}
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="secondary" onClick={handleNo}>No</Button>
                    <Button variant="contained" color="secondary" onClick={handleYes}>Yes</Button>
                </DialogActions>
            </Dialog>
        );
    };

    const handleProcessRowUpdateError = (error) => {
        console.log('error', error);
    };

    const handleActionButtonClick = React.useCallback(
        (rowInnerId, buttonId) =>
            new Promise(() => {
                setRowButtonPromiseArguments({rowInnerId, buttonId});
            }),
        [],
    );

    const renderDialogInputs = (button) => {
        if (!button.popup?.inputs)
            return null;

        const setInputValue = (rowButtonInputs, setRowButtonInputs, inputId) => {
            return (inputValue) => {
                rowButtonInputs.find(x => x.id === inputId).value = inputValue;
                setRowButtonInputs(rowButtonInputs);
            };
        };

        let res = [];
        button.popup.inputs.forEach(input => {
            // initialize this value in case it will not be touched by the user (we want it to be 0 and not undefind for example)
            rowButtonInputs.push({id: input.id, value: input.defaultValue});
            setRowButtonInputs;
            let element = getInnerElement(input.id, input.type, input.defaultValue, setInputValue(rowButtonInputs, setRowButtonInputs, input.id), colors, input.displayName, null, input.options, input.minValue, input.maxValue, false, true);
            res.push(element);
        });
        return res;
    };

    const renderActionButtonClickConfirmDialog = () => {
        if (!rowButtonPromiseArguments) {
            return null;
        }

        const handleEntered = () => {
            // The `autoFocus` is not used because, if used, the same Enter that saves
            // the cell triggers "No". Instead, we manually focus the "No" button once
            // the dialog is fully open.
            // noButtonRef.current?.focus();
        };

        const handleNo = () => {
            setRowButtonPromiseArguments(null);
        };

        const handleYes = async () => {
            setBackdropOpened(true);
            const {rowInnerId, buttonId} = rowButtonPromiseArguments;

            try {
                const button = feature.rowButtons.find(x => x.id === buttonId);
                const params = getIdsParams(feature, rowInnerId, primaryKeys);
                
                params["featureId"] = feature.id;
                params["buttonId"] = buttonId;
                
                let body = null;
                if (rowButtonInputs){
                    if(button.httpMethod === 'GET'){
                        rowButtonInputs.forEach(input => { params[`input_${input.id}`] = input.value; });
                    }
                    else{
                        body = JSON.parse(JSON.stringify(rowButtonInputs));
                    }
                }

                try {
                    setRowButtonPromiseArguments(null);
                    setRowButtonInputs([]);
                    let data = await apiCall({
                        method: button.httpMethod,
                        api: `features/${button.api}`,
                        body,
                        params,
                        environment: env,
                        gameKey: gameKey
                    });

                    if (button.type === 'info')
                        setRowValuesData(data);

                    setBackdropOpened(false);
                } catch (error) {
                    setErrors({title: 'Update Failed!!!', errors: [error.error]});
                    setRowButtonPromiseArguments(null);
                    setBackdropOpened(false);
                }

            } catch (error) {
                console.log('error on row button call', error);
                // nothing to do here
            }

            setRowButtonInputs([]);
        };

        const {buttonId} = rowButtonPromiseArguments;
        let button = feature.rowButtons.find(x => x.id === buttonId);

        return (
            <Dialog
                maxWidth="xs"
                TransitionProps={{onEntered: handleEntered}}
                open={!!rowButtonPromiseArguments}
            >
                <DialogTitle
                    backgroundColor={colors.primary[400]}>{`${button.popup?.title}` ?? 'Are you sure?'}</DialogTitle>
                <DialogContent dividers>
                    {`Pressing 'Yes' will ${button.hoverText}.`}
                    {renderDialogInputs(button)}
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="secondary" ref={noButtonRef} onClick={handleNo}>
                        No
                    </Button>
                    <Button variant="contained" color="secondary" onClick={handleYes}>Yes</Button>
                </DialogActions>
            </Dialog>
        );
    };

    const renderErrorsDialog = () => {

        if (!errors)
            return null;

        return (
            <Dialog fullWidth={true} onClose={() => {
            }} open={true}>
                <Box backgroundColor={colors.primary[400]} display="flex" alignItems="start"
                     justifyContent="space-between">
                    <Header title={errors.title}/>
                </Box>
                <List disablePadding={true}>
                    {getErrorsGrid(errors.errors, colors)}
                </List>
                <Button variant="contained" color="secondary" onClick={() => setErrors(null)}>Close</Button>
            </Dialog>);
    };

    const renderCreateWizard = () => {
        if (!rowValuesData)
            return null;

        return (
            <Dialog fullWidth={true} onClose={() => {}} open={true}>
                <Box backgroundColor={colors.primary[400]} display="flex" alignItems="start"
                     justifyContent="space-between">
                    <Header title={rowValuesData.displayName}/>
                </Box>
                {getRowValuesGrid(rowValuesData)}
                <Button variant="contained" color="secondary" onClick={() => {
                    setRowValuesData(undefined);
                }}>Close</Button>
            </Dialog>);

    };

    const equalValuesAggregation = {
        apply: (params) => {
            if (params.values.length === 0) {
                return null;
            }

            if (params.values.every((value) => value === params.values[0])) {
                return params.values[0];
            }

            return null;
        },
        label: "equalValues",
        columnTypes: ["string", "number"]
    };

    return (createMode
        ? <FeatureWizard gameKey={gameKey} env={env} feature={feature} setBackdropOpened={setBackdropOpened}
                         backdropOpened={backdropOpened}/>
        : <Box m="20px">
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Header title={feature.displayName} subtitle={feature.description}/>
                <Box>
                    {getActionButtons(feature, colors)}
                </Box>
            </Box>
            <Box display="flex" justifyContent="flex-start" alignItems="start" borderRadius="3px">
                {searchElements}
            </Box>
            <Box
                m="40px 0 0 0"
                height="75vh"
                sx={{
                    "& .MuiDataGrid-root": {
                        border: "none",
                    },
                    "& .MuiDataGrid-cell": {
                        borderBottom: "none",
                    },
                    "& .name-column--cell": {
                        color: colors.greenAccent[300],
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: colors.blueAccent[700],
                        borderBottom: "none"
                    },
                    "& .MuiDataGrid-virtualScroller": {
                        backgroundColor: colors.primary[400],
                    },
                    "& .MuiDataGrid-footerContainer": {
                        borderTop: "none",
                        backgroundColor: colors.blueAccent[700],
                    },
                    "& .MuiCheckbox-root": {
                        color: `${colors.greenAccent[200]} !important`,
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                        color: `${colors.grey[100]} !important`,
                    },
                    ".MuiDataGrid-aggregationColumnHeaderLabel": {
                        display: "none"
                    }
                }}
            >
                {renderSelectedRowsActionConfirmDialog()}
                {renderCreateWizard()}
                {renderRowUpdateConfirmDialog()}
                {renderActionButtonClickConfirmDialog()}
                {renderErrorsDialog()}
                <DataGridPremium
                    //defaultGroupingExpansionDepth={-1} // this will show all rows expended by default
                    isRowSelectable={(params) => {
                        if(isGroupingDisabled(feature) || params.id?.startsWith('auto-generated')) 
                            return true;
                    }}
                    disableRowGrouping={isGroupingDisabled(feature)}
                    getRowId={(row) => getManipulatedRowId(row)}
                    rows={featureData}
                    columns={columns}
                    initialState={initialState}
                    components={{Toolbar: GridToolbar}}
                    rowGroupingColumnMode="multiple"
                    rowGroupingModel={rowGroupingModel}
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={handleProcessRowUpdateError}
                    aggregationFunctions={{
                        ...GRID_AGGREGATION_FUNCTIONS,
                        equalValues: equalValuesAggregation
                    }}
                    getAggregationPosition={(groupNode) => groupNode == null ? null : "inline"}
                    aggregationModel={aggregationModel}
                    checkboxSelection={feature.wizardData.showDeleteMultipleButton ?? false}
                    onRowSelectionModelChange={(newRowSelectionModel) => {
                        if(isGroupingDisabled(feature)){ // regular rows
                            setSelectedRows(newRowSelectionModel);
                        }
                        else{ // aggregated rows
                            if(newRowSelectionModel?.length > 0){
                                let newRowsSelection = newRowSelectionModel.filter(x => x.startsWith('auto-generated'));
                                if(newRowsSelection?.length > 0){
                                    setSelectedRows(newRowSelectionModel);
                                }
                            }
                        }
                    }}
                    rowSelectionModel={selectedRows}
                />
            </Box>
        </Box>);
};

function clearOtherSearchFields(fieldId, fieldIdToHook) {
    Object.keys(fieldIdToHook).forEach(currentFieldId => {
        if (currentFieldId !== fieldId) {
            fieldIdToHook[currentFieldId](""); // use setState to clear the value
        }
    });
}

function isGroupingDisabled(feature) {
    return !feature.wizardData.aggregateDisplay;
}

function getPrimaryKeys(feature) {
    let res = [];
    getFieldsArray(feature).forEach(field => {
        if (field.primaryKey)
            res.push(field.key);
    });
    return res;
}

async function fetchData(gameKey, env, feature, setFeatureData, setBackdropOpened, filtersQuery) {
    try {
        setBackdropOpened(true);

        const params = Object.assign({
            featureId: feature.id
        }, filtersQuery);

        const data = await apiCall({
            method: 'GET',
            api: "features",
            environment: env,
            gameKey,
            params
        });
        const mappedIdsCount = {};
        const primaryKeys = getPrimaryKeys(feature);
        data.forEach(row => {

            let idKeys = [];
            primaryKeys.forEach(key => {
                idKeys.push(row[key]);
            });
            let id = idKeys.join('_');

            if (!Object.prototype.hasOwnProperty.call(mappedIdsCount, id)) {
                mappedIdsCount[id] = 0;
            }

            row.innerId = `${id}_${mappedIdsCount[id]}`;
            mappedIdsCount[id] += 1;
        });
        setFeatureData(data);
        setBackdropOpened(false);
    } catch (e) {
        setBackdropOpened(false);
    }
}

function getType(type) {
    switch (type) {
        case InputDataTypes.BOOL:
            return 'boolean';
        case InputDataTypes.INTEGER:
        case InputDataTypes.DECIMAL:
            return 'number';
        default:
            return 'string';
    }
}

function getFlex(type) {
    switch (type) {
        case InputDataTypes.BOOL:
            return 0.3;
        case InputDataTypes.INTEGER:
        case InputDataTypes.DECIMAL:
            return 0.5;
        case InputDataTypes.MULTI_SEGMENTS:
            return 2;
        default:
            return 1;
    }
}

function getManipulatedRowId(row) { // this method returns a fake id with a _{count} at the end
    return row.innerId;
}

async function handleRowButtonAction(rowId, gameKey, env, setFeatureData, primaryKeys, setBackdropOpened, feature, rowButton, setRowValuesData) {

    try {
        setBackdropOpened(true);
        const params = getIdsParams(feature, rowId, primaryKeys);
        params["featureId"] = feature.id;
        params["buttonId"] = rowButton.id;
        const data = await apiCall({
            method: rowButton.httpMethod,
            api: `features/${rowButton.api}`,
            params,
            gameKey,
            environment: env
        });

        if (rowButton.type === 'info') {
            setRowValuesData(data);
            setBackdropOpened(false);
        } else {
            await fetchData(gameKey, env, feature, setFeatureData, setBackdropOpened);
        }
    } catch (e) {
        setBackdropOpened(false);
    }
}

function getIdsParams(feature, rowId, primaryKeys) {
    const params = {};
    if (isGroupingDisabled(feature)) {
        const split = rowId.split('_');

        // take the key from primaryKeys and the value from the rowId
        for (let i = 0; i < primaryKeys.length; i++) {
            params[primaryKeys[i]] = split[i];
        }
    } else { // supporting only single id
        let realId = rowId;
        if(realId.includes('_'))
            realId = realId.split('_')[0];

        if(realId.includes('/'))
            realId = realId.split('/')[1];

        params[primaryKeys[0]] = realId;
    }

    return params;
}

function handleSearchRequest(gameKey, env, feature, setFeatureData, setBackdropOpened, fieldId, value) {
    let filterQuery = {};
    if (!isNullOrEmpty(value))
        filterQuery[fieldId] = value;

    fetchData(gameKey, env, feature, setFeatureData, setBackdropOpened, filterQuery);
}

async function downloadTemplateCSV(gameKey, env, feature, setBackdropOpened) {
    setBackdropOpened(true);
    await apiFileDownload({
        method: 'GET',
        api: "features/bulk",
        gameKey,
        environment: env,
        params: {featureId: feature.id}
    });
    setBackdropOpened(false);
}

function getErrorsGrid(errors, colors) {
    let res = [];
    let count = 1;
    errors.forEach(error => {
        res.push(
            <Box borderTop={1} key={'error_' + count} backgroundColor={colors.primary[400]} alignItems="start">
                <Box mb="10px">
                    <Typography variant="h4" color={colors.grey[100]} sx={{m: "0 0 5px 0"}}>
                        {error}
                    </Typography>
                </Box>
                <Typography fontStyle="italic" fontWeight="bold"
                            sx={{fontSize: 'h1.fontSize', color: colors.grey[100]}}/>
            </Box>
        );
        count++;
    });
    return res;
}

export function getRowValuesGrid(rowValuesData) {
    
    function getTableRow(rowValues, header = false) {
        let res = [];
        let count = 0;
        rowValues.forEach(x => {
            res.push(header
                ? <TableCell key={count} component="th" scope="row">{x}</TableCell>
                : <TableCell key={count} align="left">{x}</TableCell>);
                count++;
            });
            return res;
    }
        
    let rows = [];
    if (rowValuesData.displayType === 'singleRow') {
        rowValuesData.schema.forEach(x => { rows.push(getTableRow([x.displayName, rowValuesData.data[x.column] ?? 'N/A'])); });
    } else {
        rows.push(getTableRow(rowValuesData.schema.map(x => {
            return x.displayName;
        }), true));
        rowValuesData.data.forEach(row => {
            let orderedRowData = [];
            rowValuesData.schema.forEach(x => orderedRowData.push(row[x.column]));
            rows.push(getTableRow(orderedRowData));
        });
    }

    let rowsCount = 0;
    return <TableContainer component={Paper}>
        <Table sx={{minWidth: 650}} aria-label="simple table">
            <TableHead><TableRow>{rows[0]}</TableRow></TableHead>
            <TableBody>
                {rows.slice(1).map((row) => {
                    rowsCount++;
                    return (<TableRow key={rowsCount}
                                      sx={{'&:last-child td, &:last-child th': {border: 0}}}>{row}</TableRow>);
                })}
            </TableBody>
        </Table>
    </TableContainer>;
}

async function importFromTemplateCSV(file, gameKey, env, feature, setFeatureData, setBackdropOpened, setErrors) {
    setBackdropOpened(true);
    try {
        await apiFileUpload({
            api: "features/bulk",
            gameKey,
            environment: env,
            params: {featureId: feature.id},
            file
        });
        await fetchData(gameKey, env, feature, setFeatureData, setBackdropOpened);
    } catch (error) {
        if (error instanceof HttpError && error.extraData) {
            setErrors({
                title: error.message,
                errors: error.extraData.map(x => `${x.location} | ${x.message}`)
            });
        } else {
            setErrors({
                title: error.message,
                errors: [error.message]
            });
        }
    }
    setBackdropOpened(false);
}

function getFieldsArray(feature) {
    if (feature.displayFields)
        return feature.displayFields;

    return feature.fields;
}

function getRowsDiffBodyAndMutation(feature, newRow, oldRow) {
    let foundDifference = false;
    let body = {};
    let mutation;
    Object.keys(newRow).forEach(key => {
        if (newRow[key] !== oldRow[key]) {
            body[key] = newRow[key];
            foundDifference = true;
            let fieldDisplayName = feature.fields.find(x => x.id === key).displayName;
            mutation = `${fieldDisplayName} from ${oldRow[key]} to ${newRow[key]}`;
        }
    });

    if (foundDifference)
        return [body, mutation];
}

FeaturePage.propTypes = {
    backdropOpened: PropTypes.bool,
    gameKey: PropTypes.string,
    env: PropTypes.string,
    theme: PropTypes.object,
    feature: PropTypes.object,
    setBackdropOpened: PropTypes.func,
    user: PropTypes.object
};

export default FeaturePage;